import {
	apiPost,
	getApiUrl,
	type PostOrdersIdAttachmentsFetchResponse,
	postOrdersIdAttachmentsPath,
} from "Infrastructure/Api/Api";
import {
	ApiCallError,
	getMessageFromErrorResponse,
	processResponse,
} from "Infrastructure/Api/ApiUtils";
import { Resources, useResource } from "Infrastructure/Translation/Resources";
import { getOrderAttachmentsQueryKey } from "Order/Api/Query/useOrderAttachmentsQuery";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

const orderMutationsResource = Resources.base.order.mutations;

/**
 * Bypasses the generated `postOrdersIdAttachments`: its FormData serializer
 * JSON-stringifies `File[]` unless the key ends in `[]`, while the backend
 * expects repeated `attachments` parts.
 */
const postOrderAttachments = (orderId: number, files: File[]) => {
	const formData = new FormData();
	for (const file of files) {
		formData.append("attachments", file);
	}
	return apiPost(
		`${getApiUrl()}${postOrdersIdAttachmentsPath(orderId)}`,
		formData,
		new Headers(),
	) as Promise<PostOrdersIdAttachmentsFetchResponse>;
};

export const useUploadOrderAttachmentsMutation = (orderId: number) => {
	const { t } = useResource();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: processResponse((files: File[]) =>
			postOrderAttachments(orderId, files),
		),
		onError: (error) => {
			console.error(
				`${t(orderMutationsResource.orderAttachmentsUploadError)}:`,
				error,
			);
			// The cached attachment count may be stale (another admin filled a slot);
			// refetch so `remaining` reflects the server.
			queryClient.invalidateQueries({
				queryKey: getOrderAttachmentsQueryKey(orderId),
			});
			if (!(error instanceof ApiCallError)) {
				return;
			}
			// The global mutation error handler suppresses 400/401 toasts, so
			// surface those here; every other status is already toasted globally.
			const { status } = error.data;
			if (status !== 400 && status !== 401) {
				return;
			}
			toast.error(
				`${t(orderMutationsResource.orderAttachmentsUploadError)}: ${getMessageFromErrorResponse(error)}`,
			);
		},
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: getOrderAttachmentsQueryKey(orderId),
			});
			toast.success(t(orderMutationsResource.orderAttachmentsUploadSuccess));
		},
	});
};
